import * as React from 'react';
import type { BasePortletStore, PoeStateChangedCallback } from 'ehs-react';
import { createPortletStore } from 'ehs-react';
import type { at } from './generated/Beans';
import { MainView } from './MainView';

type MinimalUserData = at.sense.portlets.saf.patientselfonboarding.model.ui.MinimalUserData;

type OnboardingAgreement = at.sense.saf.settings.portal.onboarding.OnboardingAgreement;
type FrontendConfigurationBean = at.sense.portlets.saf.patientselfonboarding.model.ui.FrontendConfigurationBean;

const setDefaultCheckboxValues = (
  onboardingAgreementConfiguration: OnboardingAgreement[]
) => {
  const valuesArray: boolean[] = [];
  onboardingAgreementConfiguration.forEach(() => {
    valuesArray.push(false); // unchecked by default
  });
  return valuesArray;
};

/**
 * The Portlet store.
 *
 * It contains the portlet url, frontend configuration, orchestratable instance to send workflow events, and other state needed by everything, such as maybe a portlet-wide loadmask state.
 * This is automatically filled by the POE.
 *
 * usePortletStore is a hook for use inside react components, which will re-render whenever the state they request changes.
 * PortletStoreApi is for use in callbacks and async functions, not in the view.
 */
type PortletStore = BasePortletStore<FrontendConfigurationBean> & {
  /** The toggled agreement values. */
  agreementValues: boolean[];

  /** the saved createUser values. */
  dataForCreateUser: MinimalUserData;

  /** initializes all forms. */
  initForms: () => void;

  /** changes the agreement values. */
  setAgreementValues: (values: boolean[]) => void;

  /** returns the toggled agreements in submit format. */
  getAgreementsToSubmit: () => { [key: string]: boolean };

  /** Set the date for the createUser functionality. */
  setDataForCreateUser: (data: MinimalUserData) => void;
};

const emptySubmitData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  selectedAgreements: {}
};
export const [
  usePortletStore,
  PortletStoreApi
] = createPortletStore<PortletStore>((set, get) => ({
  agreementValues: [false],
  dataForCreateUser: emptySubmitData,
  initForms: () =>
    set({
      agreementValues: setDefaultCheckboxValues(
        (get() as PortletStore).configuration.onboardingAgreementConfiguration
      ),
      dataForCreateUser: emptySubmitData
    }),
  setAgreementValues: (values: boolean[]) =>
    set({
      agreementValues: values
    }),
  getAgreementsToSubmit: () => {
    const agreementValues: { [key: string]: boolean } = {};
    (get() as PortletStore).configuration.onboardingAgreementConfiguration.forEach(
      (field: OnboardingAgreement, index: number) => {
        agreementValues[field.name] = get().agreementValues[index];
      }
    );
    return agreementValues;
  },
  setDataForCreateUser: (data: MinimalUserData) =>
    set({ dataForCreateUser: data })
}));

/**
 * The main App component.
 *
 * It should render the MainView, possibly a portlet-wide loadmask and any global buttons.
 */
export function App(): React.JSX.Element {
  return <MainView />;
}

/**
 * Will be called upon state change of workflow.
 * @param event the event name
 */
export const poeStateChanged: PoeStateChangedCallback = async event => {
  console.debug(
    '%cPoeStateChanged triggered with event=' + event,
    'color: green'
  );
};
